import { useState } from 'react';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  SR_API_ACTION_ADDRESS_RESOLUTION_MARK_AS_RESOLVED,
  SR_API_ACTION_ADDRESS_RESOLUTION_MARK_AS_IGNORED,
  SR_API_ACTION_ADDRESS_REGISTER,
  SR_API_ACTION_ADDRESS_REGISTER_ADDRESS_STRING,
} from 'api/utilityActionTypes';
import {
  ADDRESS_RESOLUTION_REASON_MANUALLY_LINKED_EXISTING,
  ADDRESS_RESOLUTION_REASON_MANUALLY_LINKED_NEW,
} from 'common/utility/constants';

/**
 * Controller hook for address resolution operations
 * Manages state and handlers for linking addresses to resolution attempts
 *
 * @param {Object} params
 * @param {Object} params.item - The address resolution attempt item
 * @param {Function} params.reloadItems - Callback to reload items after successful resolution
 * @returns {Object} Controller API with state and handlers
 */
const useAddressResolutionController = ({ item, reloadItems }) => {
  const dispatch = useDispatch();

  // State management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successModalType, setSuccessModalType] = useState(null);
  const [showIgnoreConfirmModal, setShowIgnoreConfirmModal] = useState(false);

  /**
   * Handles linking an existing address to the resolution attempt
   * @param {Object} params
   * @param {Object} params.address - The existing address to link
   */
  const handleExistingAddressSelect = ({ address }) => {
    setErrorMessage(null);
    setIsSubmitting(true);

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_RESOLUTION_MARK_AS_RESOLVED,
      payload: {
        addressId: address.id,
        resolutionAttemptId: item.id,
        reason: ADDRESS_RESOLUTION_REASON_MANUALLY_LINKED_EXISTING,
      },
      onSuccess: () => {
        setIsSubmitting(false);
        setSuccessModalType('resolved');
        if (reloadItems) {
          reloadItems({ removeEntityId: item.id });
        }
      },
      onError: (error) => {
        setErrorMessage(error?.message || 'Failed to link address. Please try again.');
        setIsSubmitting(false);
      },
    });
  };

  /**
   * Handles creating a new address from address string and linking it
   * Two-step process:
   * 1. Register the address string to create a new address
   * 2. Link the newly created address to the resolution attempt
   *
   * @param {Object} params
   * @param {Object} params.formValues - Form values containing addressString
   */
  const handleNewAddressStringRegister = ({ formValues, onSuccess }) => {
    setErrorMessage(null);
    setIsSubmitting(true);

    // Step 1: Register the address string to create a new address
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_REGISTER_ADDRESS_STRING,
      payload: {
        addressString: formValues.addressString,
        utilityPartnerId: item.partnerId,
      },
      onSuccess: (address) => {
        // Step 2: Link the newly created address to the resolution attempt
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_ADDRESS_RESOLUTION_MARK_AS_RESOLVED,
          payload: {
            addressId: address.id,
            resolutionAttemptId: item.id,
            reason: ADDRESS_RESOLUTION_REASON_MANUALLY_LINKED_NEW,
          },
          onSuccess: () => {
            setIsSubmitting(false);
            setSuccessModalType('resolved');
            if (typeof onSuccess === 'function') {
              onSuccess();
            }
            if (reloadItems) {
              reloadItems({ removeEntityId: item.id });
            }
          },
          onError: (error) => {
            setErrorMessage(error?.message || 'Failed to link address. Please try again.');
            setIsSubmitting(false);
          },
        });
      },
      onError: (error) => {
        setErrorMessage(error?.message || 'Failed to register address. Please try again.');
        setIsSubmitting(false);
      },
    });
  };

  /**
   * Handles creating a new address from manual fields and linking it
   * Two-step process:
   * 1. Register the address fields to create a new address
   * 2. Link the newly created address to the resolution attempt
   *
   * @param {Object} params
   * @param {Object} params.addressFields - Manual address fields payload
   */
  const handleManualAddressRegister = ({ addressFields, onSuccess }) => {
    setErrorMessage(null);
    setIsSubmitting(true);

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_REGISTER,
      payload: {
        utilityPartnerId: item.partnerId,
        address: addressFields,
        addressCreationMode: 'legacy',
      },
      onSuccess: (address) => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_ADDRESS_RESOLUTION_MARK_AS_RESOLVED,
          payload: {
            addressId: address.id,
            resolutionAttemptId: item.id,
            reason: ADDRESS_RESOLUTION_REASON_MANUALLY_LINKED_NEW,
          },
          onSuccess: () => {
            setIsSubmitting(false);
            setSuccessModalType('resolved');
            if (typeof onSuccess === 'function') {
              onSuccess();
            }
            if (reloadItems) {
              reloadItems({ removeEntityId: item.id });
            }
          },
          onError: (error) => {
            setErrorMessage(error?.message || 'Failed to link address. Please try again.');
            setIsSubmitting(false);
          },
        });
      },
      onError: (error) => {
        setErrorMessage(error?.message || 'Failed to register address. Please try again.');
        setIsSubmitting(false);
      },
    });
  };

  /**
   * Handles registering a new address (manual or autocomplete) and linking it
   * @param {Object} params
   * @param {Object} params.addressFields - Manual address fields payload
   * @param {string} params.addressString - Address string from autocomplete
   */
  const handleNewAddressRegister = ({ addressFields, addressString, onSuccess }) => {
    if (addressString) {
      handleNewAddressStringRegister({ formValues: { addressString }, onSuccess });
      return;
    }

    if (!addressFields || Object.keys(addressFields).length === 0) {
      setErrorMessage('Missing manual address details. Please complete the form.');
      return;
    }

    handleManualAddressRegister({ addressFields, onSuccess });
  };

  /**
   * Handles closing the success modal
   */
  const handleCloseSuccessModal = () => {
    setSuccessModalType(null);
  };

  /**
   * Opens the ignore confirmation modal
   */
  const handleOpenIgnoreConfirm = () => {
    setShowIgnoreConfirmModal(true);
  };

  /**
   * Closes the ignore confirmation modal
   */
  const handleCloseIgnoreConfirm = () => {
    setShowIgnoreConfirmModal(false);
  };

  /**
   * Confirms ignoring the search term
   */
  const handleConfirmIgnore = () => {
    setErrorMessage(null);
    setIsSubmitting(true);

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_RESOLUTION_MARK_AS_IGNORED,
      payload: {
        resolutionAttemptId: item.id,
      },
      onSuccess: () => {
        setIsSubmitting(false);
        setShowIgnoreConfirmModal(false);
        setSuccessModalType('ignored');
        if (reloadItems) {
          reloadItems({ removeEntityId: item.id });
        }
      },
      onError: (error) => {
        setErrorMessage(error?.message || 'Failed to ignore search term. Please try again.');
        setIsSubmitting(false);
        setShowIgnoreConfirmModal(false);
      },
    });
  };

  // Return organized controller API
  return {
    state: {
      isSubmitting,
      errorMessage,
      successModalType,
      showIgnoreConfirmModal,
    },
    handlers: {
      handleExistingAddressSelect,
      handleNewAddressStringRegister,
      handleManualAddressRegister,
      handleNewAddressRegister,
      handleCloseSuccessModal,
      handleOpenIgnoreConfirm,
      handleCloseIgnoreConfirm,
      handleConfirmIgnore,
    },
  };
};

export default useAddressResolutionController;
